import { HistoryView } from "@/components/history/HistoryView";
import AntIcon from "@/components/icons/AntIcon";
import BackNavigationArrow from "@/components/views/BackNavigationArrow";
import CopyToClipboardView from "@/components/views/CopyToClipboardView";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { formatThreeSpacedString } from "@/utils/formatUtils";
import { appendSearchParamsToURL } from "@/utils/utils";
import { App as AntApp, Card, Descriptions, Flex, Tabs, TabsProps } from "antd";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useEnumeration, useGetContract, useGetHistoryLogForContract } from "../../../common/api/queries";
import { PageSizes } from "../../../common/constants";
import { Permission } from "../../../common/security/enums";
import { EntityIdObject } from "../../../common/types";
import { useHasAnyPermission, useHasPermissions, useIsCurrentAgentTypePlus } from "../../auth/hooks";
import { ContractCommissionAdvance } from "../../commissions/advance/components/ContractCommissionAdvance";
import { ProductBusinessCoefficients } from "../../commissions/productcommissionssettings/components/ProductBusinessCoefficients";
import { EnumerationType } from "../../enumerations/enums";
import { HistoryLogTable } from "../../historylog/components/HistoryLogTable";
import { useIsOverviewsEnabled } from "../../overviews/hooks";
import { formatProductName } from "../../product/utils";
import { ContractIncrements } from "../components/ContractIncrements";
import { ContractComplaints } from "../components/forms/ContractComplaints";
import { DepositContractForm } from "../components/forms/deposit/DepositContractForm";
import { GenericContractForm } from "../components/forms/generic/GenericContractForm";
import { InsuranceContractForm } from "../components/forms/insurance/InsuranceContractForm";
import { InvestmentContractForm } from "../components/forms/investment/InvestmentContractForm";
import { LoanContractForm } from "../components/forms/loan/LoanContractForm";
import { SecondPillarContractForm } from "../components/forms/secondpillar/SecondPillarContractForm";
import { ThirdPillarContractForm } from "../components/forms/thirdpillar/ThirdPillarContractForm";
import { ContractTags } from "../components/tags/ContractTags";
import { ContractAttachmentsSection } from "../components/views/detail/ContractAttachmentsSection";
import { ContractCommissionTableView } from "../components/views/detail/ContractCommissionTableView";
import { ContractHeaderActionsView } from "../components/views/detail/ContractHeaderActionsView";
import { DepositContractDataSection } from "../components/views/detail/deposit/DepositContractDataSection";
import { GenericContractDataSection } from "../components/views/detail/generic/GenericContractDataSection";
import { InsuranceContractDataSection } from "../components/views/detail/insurance/InsuranceContractDataSection";
import { InvestmentContractDataSection } from "../components/views/detail/investment/InvestmentContractDataSection";
import { LoanContractDataSection } from "../components/views/detail/loan/LoanContractDataSection";
import { SecondPillarContractDataSection } from "../components/views/detail/secondpillar/SecondPillarContractDataSection";
import { ThirdPillarContractDataSection } from "../components/views/detail/thirdpillar/ThirdPillarContractDataSection";
import { ContractType } from "../enums";
import { Contract } from "../types";
import {
  CONTACT_DETAIL_TAB,
  ContactDetailTab,
  contractPermissionsByContractType,
  isDepositContract,
  isInsuranceContract,
  isInvestmentContract,
  isLoanContract,
  isSecondPillarContract,
  isThirdPillarContract,
} from "../utils";

type UpdateContractFormProps = {
  contract: Contract;
  onCancel: () => void;
};

const UpdateContractForm = ({ contract, onCancel }: UpdateContractFormProps) => {
  if (isDepositContract(contract)) return <DepositContractForm initialContract={contract} onCancel={onCancel} />;

  if (isInsuranceContract(contract)) return <InsuranceContractForm initialContract={contract} onCancel={onCancel} />;

  if (isInvestmentContract(contract)) return <InvestmentContractForm initialContract={contract} onCancel={onCancel} />;

  if (isLoanContract(contract)) return <LoanContractForm initialContract={contract} onCancel={onCancel} />;

  if (isSecondPillarContract(contract))
    return <SecondPillarContractForm initialContract={contract} onCancel={onCancel} />;

  if (isThirdPillarContract(contract))
    return <ThirdPillarContractForm initialContract={contract} onCancel={onCancel} />;

  return <GenericContractForm initialContract={contract} onCancel={onCancel} />;
};

type ContractDetailProps = {
  contract: Contract;
};

const ContractDataSection = ({ contract }: { contract: Contract }) => {
  if (isDepositContract(contract)) return <DepositContractDataSection contract={contract} />;

  if (isInsuranceContract(contract)) return <InsuranceContractDataSection contract={contract} />;

  if (isInvestmentContract(contract)) return <InvestmentContractDataSection contract={contract} />;

  if (isLoanContract(contract)) return <LoanContractDataSection contract={contract} />;

  if (isSecondPillarContract(contract)) return <SecondPillarContractDataSection contract={contract} />;

  if (isThirdPillarContract(contract)) return <ThirdPillarContractDataSection contract={contract} />;

  return <GenericContractDataSection contract={contract} />;
};

export const ContractDetail = ({ contract }: ContractDetailProps) => {
  return (
    <>
      <ContractDataSection contract={contract} />

      <ContractAttachmentsSection contract={contract} />
    </>
  );
};

const resolveTabKey = (
  urlTabKey: string,
  hasCommissionsTabPermission: boolean,
  showContractIncrement: boolean,
  showCommissionAdvance: boolean,
): ContactDetailTab => {
  switch (urlTabKey) {
    case CONTACT_DETAIL_TAB.CONTRACT:
    case CONTACT_DETAIL_TAB.HISTORY:
    case CONTACT_DETAIL_TAB.COMPLAINTS:
    case CONTACT_DETAIL_TAB.IMPORTED_HISTORY:
      return urlTabKey;
    case CONTACT_DETAIL_TAB.INCREMENTS:
      return showContractIncrement ? urlTabKey : CONTACT_DETAIL_TAB.CONTRACT;
    case CONTACT_DETAIL_TAB.COMMISSIONS:
      return hasCommissionsTabPermission ? urlTabKey : CONTACT_DETAIL_TAB.CONTRACT;
    case CONTACT_DETAIL_TAB.COMMISSION_ADVANCE:
      return showCommissionAdvance ? urlTabKey : CONTACT_DETAIL_TAB.CONTRACT;
    default:
      return CONTACT_DETAIL_TAB.CONTRACT;
  }
};

const getSecondaryContractNumberLabel = (contract: Contract) => {
  if (isLoanContract(contract)) {
    return t("contract.attrs.loanNumber");
  }

  if (isInsuranceContract(contract)) {
    return t("contract.attrs.proposalNumber");
  }

  return t("contract.attrs.otherContractNumber");
};

const getInstitutionLabel = (contract: Contract) => {
  if (isLoanContract(contract)) {
    return t("common.bankInstitution");
  }

  if (isInsuranceContract(contract)) {
    return t("common.insuranceInstitution");
  }

  return t("common.institution");
};

export const ContractDetailContainer = () => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const { id } = useParams<EntityIdObject>() as { id: string };
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const urlTabKey = urlParams.get("tab") ?? CONTACT_DETAIL_TAB.CONTRACT;

  const [historyLogPageIndex, setHistoryLogPageIndex] = useState<number>(0);

  const { data: contract, isError } = useGetContract(id);
  const { data: historyLogData, isFetching } = useGetHistoryLogForContract({
    id,
    object: { pageSize: PageSizes.LARGE, pageIndex: historyLogPageIndex },
  });

  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const institutionsEnums = useEnumeration(EnumerationType.INSTITUTIONS);
  const productGroups = useEnumeration(EnumerationType.PRODUCT_GROUPS);
  const productEnums = useEnumeration(EnumerationType.PRODUCTS);
  const hasCommissionsTabPermission = useHasAnyPermission(Permission.PROFILE_COMMISSIONS, Permission.AGENT_COMMISSIONS);
  const hasAgentCommissionsPermission = useHasPermissions(Permission.AGENT_COMMISSIONS);
  const hasCommissionAdvancePermission = useHasAnyPermission(
    Permission.COMMISSIONS,
    Permission.PROFILE_COMMISSIONS,
    Permission.AGENT_COMMISSIONS,
  );
  const isOverviewsEnabled = useIsOverviewsEnabled();
  const [updateMode, setUpdateMode] = useState(false);

  const showContractIncrement =
    isTopAgentPlus &&
    (contract?.type === ContractType.INSURANCE_CONTRACT || contract?.type === ContractType.INVESTMENT_CONTRACT);

  const commissionAdvanceContract =
    isTopAgentPlus && hasCommissionAdvancePermission && contract && isInvestmentContract(contract)
      ? contract
      : undefined;

  const items = useMemo<TabsProps["items"]>(() => {
    if (!contract) {
      return [];
    }

    const items: TabsProps["items"] = [];

    items.push({
      key: CONTACT_DETAIL_TAB.CONTRACT,
      icon: <AntIcon type="solution" />,
      label: t("contract.titles.contractData"),
      children: (
        <div className="margin-top-small">
          {updateMode ? (
            <UpdateContractForm contract={contract} onCancel={() => setUpdateMode(false)} />
          ) : (
            <ContractDetail contract={contract} />
          )}
        </div>
      ),
    });

    items.push({
      key: CONTACT_DETAIL_TAB.COMPLAINTS,
      icon: <AntIcon type="exclamation" />,
      label: t("contract.titles.complaints"),
      children: (
        <div className="margin-top-small">
          <ContractComplaints
            data={{
              id: contract.id,
              complaintNote: contract.complaintNote,
              optimisticLockVersion: contract.optimisticLockVersion,
            }}
            updatePermission={contractPermissionsByContractType[contract.type].update}
          />
        </div>
      ),
    });

    items.push({
      key: CONTACT_DETAIL_TAB.HISTORY,
      icon: <AntIcon type="history" />,
      label: t("contract.titles.history"),
      children: (
        <div className="margin-top-small">
          <HistoryView
            item={contract}
            translationRootPath="contract.attrs"
            type="contract"
            excludedFields={["commissionPaid"]}
          />
        </div>
      ),
    });

    if (historyLogData?.pageData.length) {
      items.push({
        key: CONTACT_DETAIL_TAB.IMPORTED_HISTORY,
        icon: <AntIcon type="import" />,
        label: t("contract.titles.importedHistory"),
        children: (
          <div className="margin-top-small">
            <HistoryLogTable onChangePage={setHistoryLogPageIndex} loading={isFetching} data={historyLogData} />
          </div>
        ),
      });
    }

    if (showContractIncrement) {
      items.push({
        key: CONTACT_DETAIL_TAB.INCREMENTS,
        icon: <AntIcon type="up-circle" />,
        label: t("contract.titles.increments"),
        children: (
          <div className="margin-top-small">
            <ContractIncrements contract={contract} />
          </div>
        ),
      });
    }

    if (hasCommissionsTabPermission) {
      items.push({
        key: CONTACT_DETAIL_TAB.COMMISSIONS,
        icon: <AntIcon type="euro-circle" />,
        label: t("contract.titles.commissions"),
        children: (
          <div className="margin-top-small">
            <ContractCommissionTableView />
          </div>
        ),
      });
    }

    if (commissionAdvanceContract) {
      items.push({
        key: CONTACT_DETAIL_TAB.COMMISSION_ADVANCE,
        icon: <AntIcon type="reconciliation" />,
        label: t("contract.titles.commissionAdvance"),
        children: (
          <div className="margin-top-small">
            <ContractCommissionAdvance contract={commissionAdvanceContract} />
          </div>
        ),
      });
    }

    return items;
  }, [
    contract,
    updateMode,
    historyLogData,
    isFetching,
    hasCommissionsTabPermission,
    showContractIncrement,
    commissionAdvanceContract,
  ]);

  const tabKey = resolveTabKey(
    urlTabKey,
    hasCommissionsTabPermission,
    showContractIncrement,
    !!commissionAdvanceContract,
  );

  const moveViewToTab = (tabKey: string) => {
    navigate(appendSearchParamsToURL({ tab: tabKey, pageIndex: undefined }), { replace: true });

    if (tabKey !== CONTACT_DETAIL_TAB.CONTRACT) {
      setUpdateMode(false);
    }
  };

  const handleTabKeyChange = (key: string) => {
    if (tabKey === CONTACT_DETAIL_TAB.CONTRACT && updateMode) {
      modal.confirm({
        title: t("contract.helpers.unsavedChanges"),
        okText: t("contract.helpers.unsavedChangesConfirm"),
        cancelText: t("common.back"),
        onOk: () => moveViewToTab(key),
      });
    } else {
      moveViewToTab(key);
    }
  };

  return (
    <ContentWrapper>
      <DisplayWrapper itemLoaded={!!contract} isError={isError}>
        {contract && (
          <Card
            className="card-box"
            styles={{ title: { fontWeight: 400 } }}
            title={
              <BackNavigationArrow>
                <Flex align="center">
                  <h2 className="margin-right-small">{t("contract.enums.contractType." + contract.type)}</h2>

                  <ContractTags contract={contract} />
                </Flex>
              </BackNavigationArrow>
            }
            extra={
              !updateMode && (
                <ContractHeaderActionsView
                  contract={contract}
                  onUpdate={() => {
                    setUpdateMode(true);
                    moveViewToTab(CONTACT_DETAIL_TAB.CONTRACT);
                  }}
                />
              )
            }
          >
            <ItemCreatedUpdatedInfoView item={contract} className="margin-bottom-small" />

            <Descriptions
              bordered
              layout="vertical"
              style={{ textAlign: "center" }}
              styles={{
                label: { textAlign: "center" },
                content: { fontSize: 21 },
              }}
              size="small"
              column={5}
              className="margin-top-medium margin-bottom-medium"
              items={[
                {
                  label: t("contract.attrs.contractNumber"),
                  children: contract.contractNumber ? (
                    <CopyToClipboardView
                      content={contract.contractNumber}
                      wrapper={formatThreeSpacedString(contract.contractNumber)}
                    />
                  ) : undefined,
                },
                {
                  label: getSecondaryContractNumberLabel(contract),
                  children: contract.secondaryContractNumber ? (
                    <CopyToClipboardView
                      content={contract.secondaryContractNumber}
                      wrapper={formatThreeSpacedString(contract.secondaryContractNumber)}
                    />
                  ) : undefined,
                },
                {
                  label: getInstitutionLabel(contract),
                  children:
                    contract.institution.name +
                    (institutionsEnums.find((i) => i.id === contract.institution.id)?.deactivated
                      ? ` (${t("institution.helpers.deactivated")})`
                      : ""),
                },
                isInsuranceContract(contract) && {
                  label: t("contract.attrs.productGroup"),
                  children: productGroups.find(
                    (group) =>
                      productEnums.find((product) => contract.product.id === product.id)?.productGroupId === group.id,
                  )?.name,
                },
                {
                  label: (
                    <>
                      {t("contract.attrs.product")}
                      {isOverviewsEnabled && isTopAgentPlus && hasAgentCommissionsPermission && (
                        <span className="margin-left-tiny">
                          <ProductBusinessCoefficients productId={contract.product.id} />
                        </span>
                      )}
                    </>
                  ),
                  children: formatProductName(contract.product),
                },
              ].filter((item) => !!item)}
            />

            <Tabs activeKey={tabKey} onChange={handleTabKeyChange} items={items} />
          </Card>
        )}
      </DisplayWrapper>
    </ContentWrapper>
  );
};
